import { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import kanbanService from '../../service/kanban.service';
import { useLoader } from '../../provider/LoaderProvider';

import useParam from '../../context/ParamContext';
import useUsers from '../../context/UsersContext';
import {
  ViewType,
  KanbanType,
  conditions,
  KanbanFilterFormState,
  NavigateUrlToKanbanType,
  KanbanDialog,
  KanbanTypeToSubMenuUrl,
  AddIconVisibleForTypes,
} from '../../config/kanban';
import { generateAutocompleteItems, generateFilter, getKanbanFieldsByControl } from '../../utils/helper';
import { ColorPicker } from '../../config/ColorPicker';
import useSaveView from '../../hooks/useSaveView';
import useView from '../../hooks/useView';
import Card from './Card';
import { AutoCompleteSelectUI, ColorDialogUI, DebouncedInputUI, FormLabelUI, SelectUI, SwitchUI } from '../Interface';
import Dialog from './Dialog';
import MessageDialog from './MessageDialog';
import masterDataService from '../../service/masterData.service';
import { IconButton, Slider, Grid, Chip } from '@mui/material';
import StatusDialog from '../Status/StatusDialog';
import { ProjectPage } from '../Project/ProjectPage';
import { ResourceOnePage } from '../Resources/NewResourceOne/ResourceOnePage';
import { TaskFeedbackPage } from '../TaskFeedback/TaskFeedbackPage';
import { SimpleTaskForm } from '../NewSimpleTask/SimpleTaskForm';
import { useProject } from '../../hooks/useProject';
import { useResourceOne } from '../../hooks/useResourceOne';
import { useTaskFeedback } from '../../hooks/useTaskFeedback';
import { useSimpleTask } from '../../hooks/useSimpleTask';
import AddDialog from './AddDialog';
import TaskListReportDialog from '../TaskListReport/TaskListReportDialog';
import { getResponsibleList } from '../../utils/getResponsibleList';
import { TaskResponsibleTypeEnum } from '../../config/taskList';
import EditNotificationsIcon from '@mui/icons-material/EditNotifications';
import SimpleTaskDeadLineNotificationDialog from '../SimpleTaskDeadLineNotification/SimpleTaskDeadLineNotificationDialog';
import useSimpleTaskDeadLineNotify from '../../context/SimpleTaskDeadLineNotifyContext';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import CardMenu from './CardMenu';
import { ProjectTaskPage } from '../ProjectTasks/ProjectTaskPage';
import { sxConfig } from './kanban.style';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { colorLinkType } from '../../config/app';
import CommentDialog from '../Comment/CommentDialog';

const ComponentMap = {
  [KanbanType.PROJECT]: ProjectPage,
  [KanbanType.RESOURCE]: ResourceOnePage,
  [KanbanType.FEEDBACK]: TaskFeedbackPage,
  [KanbanType.TASK]: SimpleTaskForm,
  [KanbanType.TASKLIST]: ProjectTaskPage,
};

const NewKanbanView = () => {
  const projectState = useProject();
  const resourceOneState = useResourceOne();
  const taskFeedbackState = useTaskFeedback();
  const simpleTaskState = useSimpleTask();
  const { getUser, user, getRole } = useUsers();
  const { subMenuName, projectTypeId } = useParams();
  const { getSetSimpleTaskDeadLineNotify, simpleTaskDeadLineNotify } = useSimpleTaskDeadLineNotify();
  const saveView = useSaveView(user);
  const kanbanFilter = useView('kanbanFilter', user);
  const { getParam, params } = useParam();
  const { showLoader, hideLoader } = useLoader();
  const [kanbanData, setKanbanData] = useState([]);
  const [item, setItem] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [filterFormState, setFilterFormState] = useState({});
  const [loading, setLoading] = useState(null);
  const [type, setType] = useState('');
  const [defaultFilterFields, setDefaultFilterFields] = useState([]);
  const [taskChanged, setTaskChanged] = useState(false);
  const [messageData, setMessageData] = useState({
    addressedTo: null,
    eventData: {},
  });
  const [status, setStatus] = useState({});
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [autoCompleteOptions, setAutoCompleteOptions] = useState({
    user: [],
    company: [],
    project: [],
    responsible: [],
  });
  const [autoCompleteData, setAutoCompleteData] = useState({});
  const [refetch, setRefetch] = useState(false);
  const [roles, setRoles] = useState({});
  const [colorDialogOpen, setColorDialogOpen] = useState({
    open: false,
    lineId: null,
    subMenuUrl: '',
    type: '',
  });
  const [dialogParams, setDialogParams] = useState(null);
  const [addDialogData, setAddDialogData] = useState({ open: false, dialogData: {} });
  const [reportDialogOpen, setReportDialogOpen] = useState({ open: false, data: {} });
  const [isLoading, setIsLoading] = useState(true);
  const [responsibleType, setResponsibleType] = useState(null);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const defSlideCount = 3;
  const maxSlideCount = 3;
  const [slideCount, setSlideCount] = useState(defSlideCount);
  const [collapsedCols, setCollapsedCols] = useState([]);
  const [menuPosition, setMenuPosition] = useState(null);
  const [cardMenuData, setCardMenuData] = useState({});
  const defCommentDialogData = {
    open: false,
    lineId: null,
    subMenuUrl: '',
  };
  const [commentDialogData, setCommentDialogData] = useState(defCommentDialogData);

  const [query, setQuery] = useState({
    filter: [],
  });

  useEffect(() => {
    const kanbanDialogConfig = KanbanDialog.find((card) => card.type === type);
    const paramName = kanbanDialogConfig?.paramName;

    if (params[paramName]) {
      const parsedParams = JSON.parse(params[paramName]);
      setDialogParams(parsedParams);
    }
  }, [params, type]);

  useEffect(() => {
    if (!Object.keys(user).length) getUser();
    if (!Object.keys(simpleTaskDeadLineNotify).length) getSetSimpleTaskDeadLineNotify();
    getParam();
  }, []);

  useEffect(() => {
    if (!Object.keys(params).length) return;
    if (type !== '') return;

    const defaultFromParam = params.KANBAN_DEFAULT_VIEW;

    setType(defaultFromParam || KanbanType.TASK);
  }, [params, type]);

  useEffect(() => {
    if (Object.keys(user).length > 0) {
      const roles = getRole([
        'TASK_KANBAN_SEE_ONLY_OWN',
        'TASKLIST_KANBAN_SEE_ONLY_OWN',
        'TASKLIST_KANBAN_SEE_ONLY_OWN_AND_GROUP',
        'TASK_KANBAN_EDIT_DEADLINE_NOTIFICATION',
        'TASK_KANBAN_SEE_ONLY_OWNED',
      ]);

      setRoles(roles);
      initDefaultValues(roles);
    }
  }, [user.userId]);

  useEffect(() => {
    if (subMenuName) {
      const resolvedType = NavigateUrlToKanbanType[subMenuName] || KanbanType.TASK;
      setType(resolvedType);
    }
  }, [subMenuName]);

  useEffect(() => {
    if (subMenuName) return;
    if (!Object.keys(kanbanFilter).length) return;

    const excludedViews = params.KANBAN_EXCLUDE_VIEWS
      ? JSON.parse(params.KANBAN_EXCLUDE_VIEWS).map((e) => e.viewName)
      : [];

    const savedType = kanbanFilter?.viewType;
    if (!savedType) return;

    const defaultFromParam = params.KANBAN_DEFAULT_VIEW;

    if (excludedViews.includes(savedType)) {
      setType(defaultFromParam || KanbanType.TASK);
      return;
    }

    setType(savedType);
  }, [kanbanFilter, subMenuName, params]);

  useEffect(() => {
    if (taskChanged) {
      getItemsByType();
      setTaskChanged(false);
    }
  }, [taskChanged]);

  const kanbanViewAccess =
    params.KANBAN_VIEW_ACCESS && params.KANBAN_VIEW_ACCESS.split(',').map((access) => parseInt(access));

  const loadResponsiblePersonValue = params.KANBAN_RESPONSIBLE_PERSON_DEFAULT;

  const hasAccess = kanbanViewAccess && kanbanViewAccess.includes(user.userId) ? 'Y' : 'N';

  useEffect(() => {
    if (isLoading) return;
    if (!type) return;

    const base = KanbanFilterFormState[type] ?? {};
    const next = { ...base };

    if (type === KanbanType.TASKLIST) {
      if (roles.TASKLIST_KANBAN_SEE_ONLY_OWN_AND_GROUP) {
        const userGroupsForUser = autoCompleteOptions.userGroup.map((group) => group.value);

        next.userGroupId = userGroupsForUser;
        next.responsiblePersonId = user.userId;
      } else if (roles.TASKLIST_KANBAN_SEE_ONLY_OWN) {
        next.responsiblePersonId = user.userId;
      }
    }

    if (type === KanbanType.TASKLIST || type === KanbanType.PROJECT) {
      if (projectTypeId) {
        next.projectTypeId = parseInt(projectTypeId);
      }
    }

    if (type === KanbanType.TASK) {
      if (
        loadResponsiblePersonValue === 'Y' ||
        loadResponsiblePersonValue === undefined ||
        roles.TASK_KANBAN_SEE_ONLY_OWN
      ) {
        next.resposiblePersonId = user.userId;
      }

      if (roles.TASK_KANBAN_SEE_ONLY_OWNED) {
        next.userId = user.userId;
      }
    }

    setFilterFormState(next);
  }, [type, user.userId, roles, isLoading]);

  useEffect(() => {
    const defaultFilter = [
      {
        field: 'deletedBy',
        value: null,
      },
    ];

    const currentConditions = conditions[type || KanbanType.TASK];
    const newFilter = generateFilter(filterFormState, currentConditions);
    setQuery((prev) => {
      return { ...prev, filter: [...defaultFilter, ...newFilter] };
    });
    setDefaultFilterFields(defaultFilter);
  }, [filterFormState]);

  useEffect(() => {
    if (refetch) {
      getItemsByType();
    }
  }, [refetch]);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      if (defaultFilterFields.length > 0 && query.filter.length > 0 && !isLoading) {
        getItemsByType();
      }
    }, 100);

    return () => clearTimeout(delayDebounceFn);
  }, [query, defaultFilterFields.length, isLoading]);

  const refreshKanbanBoard = params.KANBAN_BOARD_TIMED_UPDATE && Number(params.KANBAN_BOARD_TIMED_UPDATE);

  useEffect(() => {
    if (
      query &&
      type &&
      hasAccess &&
      refreshKanbanBoard &&
      typeof refreshKanbanBoard === 'number' &&
      refreshKanbanBoard > 0
    ) {
      const intervalId = setInterval(getItemsByType, refreshKanbanBoard);

      return () => clearInterval(intervalId);
    }
  }, [query, type, hasAccess]);

  const excludeViews = params.KANBAN_EXCLUDE_VIEWS ? JSON.parse(params.KANBAN_EXCLUDE_VIEWS) : [];

  const filteredViewType = useMemo(() => {
    return ViewType.filter((view) => !excludeViews.some((exclude) => exclude.viewName === view.value));
  }, [excludeViews]);

  const getCurrentViewField = () => {
    if (type === KanbanType.PROJECT) {
      return projectState.viewField;
    } else if (type === KanbanType.RESOURCE) {
      return resourceOneState.viewField;
    } else if (type === KanbanType.FEEDBACK) {
      return taskFeedbackState.viewField;
    } else if (type === KanbanType.TASK) {
      return simpleTaskState.viewField;
    }
  };

  const SpecificComponent = ComponentMap[type] ?? null;

  const initDefaultValues = (rolesParam) => {
    const promiseArray = [];

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .usersToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .companyToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectTypeToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .projectToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(
      new Promise((resolve, reject) => {
        masterDataService
          .userGroupsToForm()
          .then((data) => {
            resolve(data);
          })
          .catch((e) => {
            reject(e);
          });
      })
    );

    promiseArray.push(getResponsibleList());

    Promise.all(promiseArray)
      .then((resp) => {
        const aData = {
          user: resp[0],
          company: resp[1],
          projectType: resp[2],
          project: resp[3],
          userGroup: resp[4],
          responsible: resp[5],
        };

        const userGroup = rolesParam?.TASKLIST_KANBAN_SEE_ONLY_OWN_AND_GROUP
          ? aData.userGroup.filter((group) => group.userGroupUserLink?.some((link) => link.userId === user.userId))
          : aData.userGroup;

        setAutoCompleteData({
          ...aData,
          userGroup,
        });

        const acOptions = {
          user: generateAutocompleteItems(aData.user, 'nickName', 'userId'),
          company: generateAutocompleteItems(aData.company, 'name', 'id'),
          projectType: generateAutocompleteItems(aData.projectType, 'name', 'id'),
          project: generateAutocompleteItems(aData.project, ['projectName', 'orderNumber'], 'projectId'),
          userGroup: generateAutocompleteItems(userGroup, 'name', 'id'),
          responsible: aData.responsible,
        };
        setAutoCompleteOptions(acOptions);
      })
      .finally(() => {
        setIsLoading(false);
        //hideLoader();
      });
  };

  const getStatusByType = () => {
    setStatusDialogOpen(true);
    showLoader();
    kanbanService
      .getStatusByType(type)
      .then((data) => {
        setStatus(data);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getItemsByType = () => {
    showLoader();
    kanbanService
      .getItemsByType(type, query, hasAccess)
      .then((data) => {
        setKanbanData(data);
        setRefetch(false);
      })
      .finally(() => {
        hideLoader();
      });
  };

  const getItemById = (id) => {
    showLoader();
    kanbanService
      .getItemById(type, id)
      .then((data) => {
        setItem(data);
        setDialogOpen(true);
        handleClose();
      })
      .finally(() => {
        hideLoader();
      });
  };

  const handleDropAndStatusUpdate = (statusId, kanbanItem) => {
    setLoading(kanbanItem.id);
    const newStatus = kanbanData.find((kanbanColumn) => kanbanColumn.itemId === statusId);

    const updatedColumn = {
      ...kanbanItem,
      statusId: statusId,
      propertyId: kanbanItem.propertyId ?? null,
      preparedness: newStatus.preparedness ? Number(newStatus.preparedness) : null,
      status: {
        ...kanbanItem.status,
        itemName: newStatus.itemName,
        color: newStatus.color,
        statusId: newStatus.itemId,
      },
    };

    kanbanService
      .updateItemsByType(type, kanbanItem.id, statusId, updatedColumn)
      .then((data) => {
        if (type === KanbanType.TASKLIST) {
          setRefetch(true);
        } else {
          setKanbanData((prev) => {
            return prev.map((kanbanColumn) => {
              if (kanbanColumn.itemId === data.statusId) {
                return {
                  ...kanbanColumn,
                  items: [data, ...kanbanColumn.items.filter((item) => item.id !== kanbanItem.id)],
                };
              } else if (kanbanColumn.itemId === kanbanItem.statusId) {
                return {
                  ...kanbanColumn,
                  items: kanbanColumn.items.filter((item) => item.id !== kanbanItem.id),
                };
              }
              return kanbanColumn;
            });
          });
        }
      })
      .finally(() => {
        setLoading(null);
      });
  };

  const onDragStart = (event, kanbanItem) => {
    event.dataTransfer.setData('kanbanItem', JSON.stringify(kanbanItem));
  };

  const showMessageDialogParam = params.KANBAN_SHOW_MESSAGE_DIALOG ? JSON.parse(params.KANBAN_SHOW_MESSAGE_DIALOG) : [];

  const showMessageDialog =
    showMessageDialogParam && showMessageDialogParam.find((dialogPr) => dialogPr.kanbanType.includes(type));

  const onDragOver = (event) => {
    event.preventDefault();
  };

  const onDrop = (event, statusId) => {
    event.preventDefault();

    if (loading) {
      return;
    }

    const kanbanItem = JSON.parse(event.dataTransfer.getData('kanbanItem'));

    if (kanbanItem.statusId !== statusId) {
      handleDropAndStatusUpdate(statusId, kanbanItem);
      if (showMessageDialog) {
        setMessageDialogOpen(true);
        setMessageData((prev) => ({
          ...prev,
          eventData: kanbanItem,
        }));
      }
    }
  };

  const handleTextLikeChange = (fieldName, newValue) => {
    setFilterFormState((prev) => ({
      ...prev,
      [fieldName]: newValue,
    }));
  };

  const handleAutocompleteChange = (fieldName, newVal, reason, control) => {
    setFilterFormState((prev) => {
      if (control === 'multiAutocomplete') {
        return {
          ...prev,
          [fieldName]: reason === 'clear' ? [] : (newVal ?? []).map((v) => v.value),
        };
      }

      setResponsibleType(null);

      if (fieldName === 'responsible') {
        const fieldByType = {
          [TaskResponsibleTypeEnum.USER]: 'responsiblePersonId',
          [TaskResponsibleTypeEnum.USERGROUP]: 'responsibleUserGroupId',
          [TaskResponsibleTypeEnum.BUSINESSPARTNER]: 'responsibleCompanyId',
          [TaskResponsibleTypeEnum.WORKER]: 'responsibleWorkerId',
        };

        setResponsibleType(newVal?.type ?? null);

        const allFields = Object.values(fieldByType);
        const activeField = fieldByType[newVal?.type];

        return {
          ...prev,
          // minden responsible mező törlése
          ...Object.fromEntries(allFields.map((f) => [f, ''])),

          // csak az aktuális típus kap értéket
          ...(activeField && {
            [activeField]: reason === 'clear' ? '' : newVal?.value ?? '',
          }),
        };
      }

      return {
        ...prev,
        [fieldName]: reason === 'clear' ? '' : newVal?.value ?? '',
      };
    });
  };

  const handleDeleteItemByType = () => {
    kanbanService.deleteItemByType(type, cardMenuData.id).then(() => {
      setKanbanData((prev) =>
        prev.map((kanbanCol) => ({
          ...kanbanCol,
          items: kanbanCol.items.filter((item) => item.id !== cardMenuData.id),
        }))
      );
      handleClose();
    });
  };

  const addOrRemoveRespPerson = (userId) => {
    kanbanService.addOrRemoveRespPersonByType(type, cardMenuData.id, userId).then((data) => {
      setKanbanData((prev) =>
        prev.map((kanbanCol) => ({
          ...kanbanCol,
          items: kanbanCol.items.map((item) =>
            item.id === cardMenuData.id
              ? {
                  ...item,
                  respPersonlist:
                    Object.keys(data).length > 0
                      ? [...item.respPersonlist, data]
                      : item.respPersonlist.filter((person) => person.userId !== userId),
                }
              : item
          ),
        }))
      );
    });
  };

  const updateRespPerson = (userId) => {
    kanbanService.updateRespPersonByType(type, cardMenuData.id, userId).then((data) => {
      if (filterFormState.responsiblePersonId || filterFormState.resposiblePersonId || filterFormState.userId) {
        setRefetch(true);
        handleClose();
      } else {
        setKanbanData((prev) =>
          prev.map((kanbanCol) => ({
            ...kanbanCol,
            items: kanbanCol.items.map((item) =>
              item.id === cardMenuData.id
                ? {
                    ...item,
                    responsiblePerson: data.responsiblePerson,
                    ...(type === KanbanType.TASK
                      ? { resposiblePersonId: data.resposiblePersonId }
                      : { responsiblePersonId: data.responsiblePersonId }),
                  }
                : item
            ),
          }))
        );
      }
    });
  };

  const handleSwitchChange = (e) => {
    const { name, checked } = e.target;
    if (!name) return;

    if (!checked) {
      setFilterFormState((prev) => {
        const updatedState = { ...prev };
        delete updatedState[name];
        return updatedState;
      });
      return;
    }

    setFilterFormState((prev) => ({
      ...prev,
      [name]: checked,
    }));
  };

  const handleCollapseCol = (statusId) => {
    setCollapsedCols((prev) =>
      prev.includes(statusId) ? prev.filter((item) => item !== statusId) : [...prev, statusId]
    );
  };

  const getGridSize = () => {
    switch (slideCount) {
      case 3:
        return 12;
      case 2:
        return 6;
      case 1:
        return 4;
      default:
        return 12;
    }
  };

  const handleClick = (event, data) => {
    setMenuPosition(menuPosition === null ? { mouseX: event.clientX + 2, mouseY: event.clientY - 6 } : null);
    setCardMenuData(data);
  };

  const handleClose = () => {
    setMenuPosition(null);
    setCardMenuData({});
  };

  const handleOpenColorDialog = () => {
    setColorDialogOpen({
      open: true,
      lineId: cardMenuData.id,
      id: cardMenuData.id,
      subMenuUrl: KanbanTypeToSubMenuUrl[type],
      type: colorLinkType.KANBAN,
    });
    handleClose();
  };

  const handleOpenCommentDialog = () => {
    setCommentDialogData({
      open: true,
      lineId: cardMenuData.id,
      subMenuUrl: KanbanTypeToSubMenuUrl[type],
    });
    handleClose();
  };

  const currentTask = kanbanData.flatMap((col) => col.items).find((task) => task.id === cardMenuData.id);

  const handleRefetchColorChange = (id, color) => {
    setKanbanData((prev) =>
      prev.map((col) => ({
        ...col,
        items: col.items.map((it) => (it.id === id ? { ...it, color: color } : it)),
      }))
    );
  };

  return (
    <div className="bg-white relative w-full px-3 pb-3 font-poppins" style={{ minHeight: 'calc(100vh - 65px)' }}>
      {params.SHOW_KANBAN_TASK_DEADLINE_NOTIFICATION_ICON === 'Y' && roles.TASK_KANBAN_EDIT_DEADLINE_NOTIFICATION && (
        <div className="hidden lg:block fixed top-[10px] lg:right-[170px] z-40">
          <IconButton onClick={() => setNotificationDialogOpen(true)}>
            <EditNotificationsIcon sx={{ fontSize: '1.8rem' }} />
          </IconButton>
        </div>
      )}
      <div className="xl:left-80 z-0 xl:z-40 xl:absolute xl:-top-14">
        <div className="flex flex-col md:flex-row w-full md:gap-3">
          <div className="w-full md:w-[110px]">
            <SelectUI
              label={<FormLabelUI text="Nézet" textColor="xl:text-white" />}
              option={filteredViewType}
              value={type}
              onChange={(e) => {
                const value = e.target.value;
                setType(value);
                saveView('kanbanFilter', { viewType: value });
              }}
              sx={{
                '@media (min-width:1140px)': {
                  ...sxConfig.fieldColor,
                },
              }}
            />
          </div>
          <div className="w-full md:w-[160px]">
            {getKanbanFieldsByControl(type, 'textLike').map(([fieldName, cfg]) => (
              <DebouncedInputUI
                key={fieldName}
                label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
                debounceMs={800}
                fieldName={fieldName}
                setQuickFilterSearchValue={handleTextLikeChange}
                quickFilterSearchValue={filterFormState[fieldName]}
                sx={{
                  '@media (min-width:1140px)': {
                    ...sxConfig.fieldColor,
                  },
                }}
              />
            ))}
          </div>
          {getKanbanFieldsByControl(type, 'autocomplete').map(([fieldName, cfg]) => {
            const disabled = cfg.getDisabled(roles);

            if (cfg.shouldShow && !cfg.shouldShow(params)) return null;

            const fieldByType = {
              [TaskResponsibleTypeEnum.USER]: 'responsiblePersonId',
              [TaskResponsibleTypeEnum.USERGROUP]: 'responsibleUserGroupId',
              [TaskResponsibleTypeEnum.BUSINESSPARTNER]: 'responsibleCompanyId',
              [TaskResponsibleTypeEnum.WORKER]: 'responsibleWorkerId',
            };

            const usedFieldName = fieldName === 'responsible' ? fieldByType[responsibleType] : fieldName;

            return (
              <div key={fieldName} className={cfg.widthClass}>
                <AutoCompleteSelectUI
                  variant="standard"
                  onChange={(_e, newVal, reason) => handleAutocompleteChange(fieldName, newVal, reason, cfg.control)}
                  selectedValue={filterFormState[usedFieldName]}
                  selectedLabelValue={
                    fieldName === 'responsiblePersonId'
                      ? autoCompleteOptions[cfg.optionsKey]?.find(
                          (o) => o.value === filterFormState[usedFieldName] && o.type === responsibleType
                        )?.label
                      : autoCompleteOptions[cfg.optionsKey]?.find((o) => o.value === filterFormState[usedFieldName])
                          ?.label
                  }
                  label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
                  dataset={autoCompleteOptions[cfg.optionsKey]}
                  disabled={disabled}
                  sx={{
                    '@media (min-width:1140px)': {
                      ...sxConfig.fieldColor,
                    },
                  }}
                />
              </div>
            );
          })}
          {getKanbanFieldsByControl(type, 'multiAutocomplete').map(([fieldName, cfg]) => {
            const disabled = cfg.getDisabled(roles);

            const selectedValues = (filterFormState[fieldName] ?? []).map((id) =>
              autoCompleteOptions[cfg.optionsKey]?.find((o) => o.value === id)
            );

            if (cfg.shouldShow && !cfg.shouldShow(params)) return null;

            return (
              <div key={fieldName} className={cfg.widthClass}>
                <AutoCompleteSelectUI
                  limitTags={1}
                  variant="standard"
                  onChange={(_e, newVal, reason) => handleAutocompleteChange(fieldName, newVal, reason, cfg.control)}
                  selectedValue={selectedValues}
                  label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
                  dataset={autoCompleteOptions[cfg.optionsKey]}
                  disabled={disabled}
                  freeSolo
                  multiple
                  sx={{
                    '@media (min-width:1140px)': {
                      ...sxConfig.fieldColor,
                    },
                  }}
                />
              </div>
            );
          })}
          {getKanbanFieldsByControl(type, 'switch').map(([fieldName, cfg]) => {
            return (
              <div key={fieldName} className={cfg.widthClass}>
                <SwitchUI
                  name={fieldName}
                  checked={filterFormState[fieldName] || false}
                  label={<FormLabelUI text={cfg.label} textColor="xl:text-white" />}
                  onChange={handleSwitchChange}
                />
              </div>
            );
          })}
          <div className="mt-2 md:mt-0 w-[100px] xl:text-white">
            <p className="text-center">Zoom</p>
            <Slider
              defaultValue={defSlideCount}
              step={1}
              min={1}
              max={maxSlideCount}
              sx={{
                '@media (min-width:1140px)': {
                  color: 'white',
                },
              }}
              onChange={(e) => setSlideCount(e.target.value)}
            />
          </div>
        </div>
      </div>
      <div className="overflow-x-auto custom-scrollbar">
        <div className="w-full mt-4 xl:pt-0 flex gap-[12px]">
          {kanbanData.map((kanban, index) => {
            const statusColor = ColorPicker.find((col) => col.colorName === kanban.color);
            const secondaryColor = statusColor ?? kanban?.color;
            const isCollapsed = collapsedCols.includes(kanban.itemId);
            return (
              <div
                onClick={isCollapsed ? () => handleCollapseCol(kanban.itemId) : undefined}
                key={kanban.itemId}
                className="relative bg-[#FaFaFa] text-[#1A1A1A] transition-all"
                style={{
                  minWidth: isCollapsed ? '50px' : '370px',
                  flex: isCollapsed ? '0 0 50px' : '1 1 370px',
                  borderRadius: '8px',
                  borderTop: `7px solid ${statusColor ? statusColor?.colorCode : secondaryColor}`,
                }}
              >
                <div
                  onDragOver={(event) => onDragOver(event)}
                  onDrop={(event) => onDrop(event, kanban.itemId)}
                  style={{
                    minWidth: 'auto',
                  }}
                >
                  <div className={`flex items-center justify-between ${isCollapsed && 'flex-col-reverse'}`}>
                    <div
                      className={`p-[14px] text-[16px] relative flex justify-between items-center font-semibold break-words [overflow-wrap:anywhere] w-[100%] ${isCollapsed && 'whitespace-nowrap rotate-90 gap-3'}`}
                    >
                      <p className={`${!isCollapsed && 'line-clamp-2'}`}>{kanban.itemName}</p>
                      <div>
                        <Chip
                          label={kanban.items.length}
                          sx={{
                            background: '#f2f2f2',
                            color: '#808080',
                          }}
                        />
                      </div>
                    </div>

                    <IconButton
                      sx={{ p: 0.5, mt: isCollapsed && 2 }}
                      onClick={(e) => {
                        e.stopPropagation();
                        handleCollapseCol(kanban.itemId);
                      }}
                    >
                      {isCollapsed ? (
                        <KeyboardArrowRightIcon sx={{ fontSize: '1.7rem' }} />
                      ) : (
                        <KeyboardArrowLeftIcon sx={{ fontSize: '1.7rem' }} />
                      )}
                    </IconButton>
                  </div>
                  <div
                    className={`overflow-y-auto custom-scrollbar w-full pb-10 px-3 ${AddIconVisibleForTypes.includes(type) ? 'h-[calc(100vh-215px)]' : 'h-[calc(100vh-180px)]'}`}
                  >
                    <Grid container spacing={1}>
                      {kanban.items.map((data) => {
                        return (
                          <Grid
                            item
                            xs={getGridSize()}
                            width="100%"
                            key={data.id}
                            display={isCollapsed ? 'none' : 'block'}
                          >
                            <Card
                              type={type}
                              key={data.id}
                              data={data}
                              onDragStart={onDragStart}
                              statusColor={statusColor}
                              secondaryColor={secondaryColor}
                              params={params}
                              loading={loading === data.id ? loading : null}
                              user={user}
                              setRefetch={setRefetch}
                              autoCompleteData={autoCompleteData}
                              handleClick={handleClick}
                              slideCount={slideCount}
                              getItemById={getItemById}
                              setReportDialogOpen={setReportDialogOpen}
                            />
                          </Grid>
                        );
                      })}
                    </Grid>
                  </div>
                  <div className={`text-center ${isCollapsed ? 'hidden' : 'block'}`}>
                    {AddIconVisibleForTypes.includes(type) && (
                      <IconButton
                        onClick={() =>
                          setAddDialogData({
                            open: true,
                            dialogData:
                              type === KanbanType.PROJECT && params.PROJECT_SHOW_PROJECT_TYPE_STATUS
                                ? {
                                    projectTypeId: filterFormState.projectTypeId,
                                    projectTypeStatusId: kanban.itemId,
                                  }
                                : { statusId: kanban.itemId },
                          })
                        }
                      >
                        <AddCircleOutlineIcon sx={{ color: '#5c5c5c' }} />
                      </IconButton>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <Dialog
        open={dialogOpen}
        data={item}
        handleClose={() => {
          setDialogOpen(false);
          setItem({});
        }}
        type={type}
        params={params}
        changed={{ taskChanged, setTaskChanged }}
        getCurrentViewField={getCurrentViewField}
        SpecificComponent={SpecificComponent}
        dialogParams={dialogParams}
      />

      <AddDialog
        addDialogData={addDialogData}
        handleClose={() => {
          setAddDialogData({ open: false, dialogData: {} });
        }}
        changed={{ taskChanged, setTaskChanged }}
        getCurrentViewField={getCurrentViewField}
        SpecificComponent={SpecificComponent}
      />

      <TaskListReportDialog
        open={reportDialogOpen.open}
        handleClose={() => {
          setReportDialogOpen((prev) => ({
            ...prev,
            open: false,
          }));
        }}
        setTaskChanged={setTaskChanged}
        data={reportDialogOpen.data}
      />

      <MessageDialog
        open={messageDialogOpen}
        data={item}
        handleClose={() => {
          setMessageDialogOpen(false);
        }}
        type={type}
        params={params}
        message={{ messageData, setMessageData }}
        user={user}
        setRefetch={setRefetch}
      />

      <StatusDialog
        open={statusDialogOpen}
        handleClose={() => setStatusDialogOpen(false)}
        data={status}
        setDataChanged={setTaskChanged}
      />

      {commentDialogData.open && (
        <CommentDialog dialogData={commentDialogData} handleClose={() => setCommentDialogData(defCommentDialogData)} />
      )}

      <ColorDialogUI
        open={colorDialogOpen.open}
        handleClose={() => setColorDialogOpen({ open: false, lineId: null, subMenuUrl: '', type: '' })}
        data={colorDialogOpen}
        setKanbanData={setKanbanData}
        refetchData={handleRefetchColorChange}
      />

      <SimpleTaskDeadLineNotificationDialog
        open={notificationDialogOpen}
        handleClose={() => setNotificationDialogOpen(false)}
      />

      <CardMenu
        handleClose={handleClose}
        open={Boolean(menuPosition)}
        menuPosition={menuPosition}
        getItemById={getItemById}
        handleDeleteItemByType={handleDeleteItemByType}
        autoCompleteData={autoCompleteData}
        addOrRemoveRespPerson={addOrRemoveRespPerson}
        currentTask={currentTask}
        updateRespPerson={updateRespPerson}
        type={type}
        handleOpenColorDialog={handleOpenColorDialog}
        handleOpenCommentDialog={handleOpenCommentDialog}
      />
    </div>
  );
};

export default NewKanbanView;
